import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { fetchWrapper } from '~/utils/fetchWrapper'

export type ScheduleKind = 'ONCE' | 'CRON' | 'INTERVAL'
export type ScheduleStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED' | 'COMPLETED' | 'ERRORED'

export interface ScheduleSpec {
  schedule_kind: ScheduleKind
  cron_expression?: string | null
  interval_seconds?: number | null
  run_at?: string | null
  timezone: string
  start_at?: string | null
  end_at?: string | null
  max_runs?: number | null
}

export interface ScheduledTask {
  uuid: string
  name: string
  description?: string | null
  cmd_type: string
  conn_type: string
  schedule_kind: ScheduleKind
  cron_expression?: string | null
  interval_seconds?: number | null
  run_at?: string | null
  timezone: string
  start_at?: string | null
  end_at?: string | null
  max_runs?: number | null
  status: ScheduleStatus
  next_run_time?: string | null
  last_run_time?: string | null
  last_spawned_task_id?: number | null
  run_count: number
  failure_count: number
  last_error?: string | null
  created_time: string
  updated_time: string
}

export interface RecentRun {
  task_id: number
  status: string
  cmd_type: string
  created_time: string
  updated_time: string
}

export interface ScheduledTaskDetail extends ScheduledTask {
  recent_runs: RecentRun[]
}

export interface ListScheduledTasksResponse {
  schedules: ScheduledTask[]
  total: number
}

export interface CreateScheduledTaskPayload {
  name: string
  description?: string
  cmd_type: string
  conn_type: string
  agent_request_json: Record<string, unknown>
  conversation_id?: number | null
  schedule: ScheduleSpec
}

export interface UpdateScheduledTaskPayload {
  name?: string
  description?: string
  action?: 'pause' | 'resume'
  schedule?: ScheduleSpec
}

const getBase = (workspaceUuid: string | undefined) =>
  workspaceUuid ? `/api/workspace/${workspaceUuid}/scheduled-tasks` : null

export const QUERY_KEY = ['scheduledTasks'] as const

export const useScheduledTasks = (statuses?: ScheduleStatus[]) => {
  const { currentWorkspace } = useWorkspaceContext()
  const base = getBase(currentWorkspace?.uuid)
  const statusParam = statuses?.length ? `?statuses=${statuses.join(',')}` : ''
  return useQuery<ListScheduledTasksResponse, Error>({
    queryKey: [...QUERY_KEY, currentWorkspace?.uuid, statuses?.join(',') ?? 'all'],
    queryFn: () => {
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.get<ListScheduledTasksResponse>(`${base}${statusParam}`)
    },
    enabled: !!base
  })
}

export const useScheduledTask = (uuid: string | undefined) => {
  const { currentWorkspace } = useWorkspaceContext()
  const base = getBase(currentWorkspace?.uuid)
  return useQuery<ScheduledTaskDetail, Error>({
    queryKey: [...QUERY_KEY, 'detail', currentWorkspace?.uuid, uuid],
    queryFn: () => {
      if (!base || !uuid) throw new Error('Workspace or uuid not found')
      return fetchWrapper.get<ScheduledTaskDetail>(`${base}/${uuid}`)
    },
    enabled: !!base && !!uuid
  })
}

export const useCreateScheduledTask = () => {
  const { currentWorkspace } = useWorkspaceContext()
  const qc = useQueryClient()
  return useMutation<ScheduledTask, Error, CreateScheduledTaskPayload>({
    mutationFn: payload => {
      const base = getBase(currentWorkspace?.uuid)
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.post<ScheduledTask>(base, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })
}

export const useUpdateScheduledTask = () => {
  const { currentWorkspace } = useWorkspaceContext()
  const qc = useQueryClient()
  return useMutation<ScheduledTask, Error, { uuid: string; payload: UpdateScheduledTaskPayload }>({
    mutationFn: ({ uuid, payload }) => {
      const base = getBase(currentWorkspace?.uuid)
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.patch<ScheduledTask>(`${base}/${uuid}`, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })
}

export const useCancelScheduledTask = () => {
  const { currentWorkspace } = useWorkspaceContext()
  const qc = useQueryClient()
  return useMutation<void, Error, { uuid: string }>({
    mutationFn: ({ uuid }) => {
      const base = getBase(currentWorkspace?.uuid)
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.delete(`${base}/${uuid}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })
}
